/**
 * PPM Inbox — Recommendations requiring action.
 * Shows pending recommendations with SLA countdown, confidence badges, and source context.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  PencilSquareIcon,
  DocumentPlusIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const TYPE_CFG = {
  update_policy:  { label: 'Update Policy',  Icon: PencilSquareIcon,    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  create_policy:  { label: 'New Policy',     Icon: DocumentPlusIcon,    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  review_policy:  { label: 'Review Policy',  Icon: MagnifyingGlassIcon, color: 'bg-slate-100 text-slate-600 border-slate-200' },
  no_action:      { label: 'No Action',      Icon: CheckCircleIcon,     color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
};

const CONF_CFG = {
  high:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low:    'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_CFG = {
  pending:          { label: 'Pending', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  owner_decided:    { label: 'Owner Decided', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  compliance_review:{ label: 'Compliance Review', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved:         { label: 'Approved', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:         { label: 'Rejected', color: 'bg-red-50 text-red-600 border-red-200' },
  expired:          { label: 'Expired', color: 'bg-slate-100 text-slate-500 border-slate-200' },
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - now) / (24 * 60 * 60 * 1000));
}

function timeAgo(date) {
  if (!date) return '';
  const ms = Date.now() - new Date(date).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

// The evidence log — every ingested document's disposition. Where "reviewed,
// no policy impact" lives so the inbox doesn't have to. Collapsed by default.
function DocumentLog() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open || rows !== null) return;
    fetch(`${API}/api/ppm/triage?limit=50`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setRows(d.triage || []))
      .catch(() => setRows([]));
  }, [open, rows]);

  const analyze = async (id) => {
    setBusyId(id);
    try {
      const res = await fetch(`${API}/api/ppm/triage/${id}/analyze`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      alert(d.success
        ? (d.recommendations_created > 0
          ? `Full analysis ran — ${d.recommendations_created} recommendation(s) created. Check the Pending tab.`
          : 'Full analysis ran — it also found no policy action needed.')
        : (d.message || 'Could not analyze.'));
      setRows(null); // reload on next open
    } catch { alert('Could not analyze.'); }
    setBusyId(null);
  };

  const DISP = {
    no_policy_impact: { label: 'No policy impact', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
    covered:          { label: 'Covered by current policy', cls: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
    material:         { label: 'Analyzed', cls: 'bg-blue-50 text-blue-600 border-blue-200' },
    error_failopen:   { label: 'Analyzed (gate offline)', cls: 'bg-amber-50 text-amber-600 border-amber-200' },
  };

  return (
    <div className="mt-8">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="text-xs font-semibold text-slate-400 hover:text-slate-600 transition-colors">
        {open ? '▾' : '▸'} Document log — how every received document was handled (audit evidence)
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {rows === null && <p className="text-xs text-slate-400">Loading…</p>}
          {rows !== null && rows.length === 0 && <p className="text-xs text-slate-400">No documents triaged yet.</p>}
          {(rows || []).map(t => {
            const d = DISP[t.disposition] || DISP.material;
            return (
              <div key={t.id} className="bg-white rounded-xl border border-slate-200 px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${d.cls}`}>{d.label}</span>
                  {t.category && <span className="text-[10px] text-slate-400 uppercase tracking-wide">{t.category.replace(/_/g, ' ')}</span>}
                  <span className="text-xs font-medium text-slate-700 truncate">{t.title}</span>
                </div>
                {t.reason && <p className="text-xs text-slate-500 mt-1">{t.reason}</p>}
                {(t.disposition === 'no_policy_impact') && !t.escalated_at && (
                  <button type="button" onClick={() => analyze(t.id)} disabled={busyId === t.id}
                    className="mt-1.5 text-[11px] font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50">
                    {busyId === t.id ? 'Analyzing…' : 'Disagree? Run full analysis'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function PPMInbox({ onOpenRecommendation, onOpenPolicy }) {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter
        ? `${API}/api/ppm/recommendations?status=${statusFilter}`
        : `${API}/api/ppm/recommendations`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setRecommendations(data.recommendations || []);
    } catch {}
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = recommendations.filter(r => r.status === 'pending').length;

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">

        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-bold text-slate-800 tracking-tight">Policy Inbox</h2>
            <p className="text-slate-400 text-xs mt-0.5">
              {pendingCount > 0 ? `${pendingCount} recommendation${pendingCount !== 1 ? 's' : ''} awaiting action` : 'All caught up'}
            </p>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 mb-6 p-1 bg-slate-100/80 rounded-2xl w-fit flex-wrap">
          {[
            { key: 'pending', label: 'Pending' },
            { key: 'owner_decided', label: 'Decided' },
            { key: 'approved', label: 'Approved' },
            { key: 'rejected', label: 'Rejected' },
            { key: '', label: 'All' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                statusFilter === tab.key
                  ? 'bg-gradient-to-r from-orange-600 to-amber-500 text-white shadow-lg shadow-blue-600/20'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 animate-pulse">
                <div className="h-4 bg-slate-100 rounded w-1/3 mb-3" />
                <div className="h-3 bg-slate-100 rounded w-2/3" />
              </div>
            ))}
          </div>
        )}

        {!loading && recommendations.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl flex items-center justify-center text-emerald-600">
              <CheckCircleIcon className="w-8 h-8" strokeWidth={1.75} />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">No recommendations</h3>
            <p className="text-sm text-slate-400">{statusFilter ? 'No recommendations match this filter.' : 'PSI will generate policy recommendations when documents are ingested.'}</p>
          </div>
        )}

        {!loading && recommendations.length > 0 && (
          <div className="space-y-3">
            {recommendations.map(rec => {
              const typeCfg = TYPE_CFG[rec.type] || TYPE_CFG.review_policy;
              const confCfg = CONF_CFG[rec.confidence] || CONF_CFG.low;
              const statusCfg = STATUS_CFG[rec.status] || STATUS_CFG.pending;
              const daysLeft = daysUntil(rec.sla_due_date);
              const overdue = daysLeft !== null && daysLeft < 0;
              const urgent = daysLeft !== null && daysLeft <= 2 && daysLeft >= 0;

              return (
                <button
                  key={rec.id}
                  onClick={() => onOpenRecommendation(rec.id)}
                  className="w-full text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-slate-300 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Badges row */}
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${typeCfg.color}`}>
                          {typeCfg.Icon && <typeCfg.Icon className="w-3.5 h-3.5" strokeWidth={1.75} />} {typeCfg.label}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${confCfg}`}>
                          {rec.confidence}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusCfg.color}`}>
                          {statusCfg.label}
                        </span>
                        {rec.source_app && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-50 text-slate-400 border border-slate-200 uppercase tracking-wide">
                            from {rec.source_app}
                          </span>
                        )}
                      </div>

                      {/* Summary */}
                      <h3 className="text-sm font-semibold text-slate-800 group-hover:text-blue-700 transition-colors mb-1">
                        {rec.summary}
                      </h3>

                      {/* Policy & mandate context */}
                      <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                        {rec.policy_number && (
                          <span>Policy: <span className="font-medium text-slate-600">{rec.policy_number}</span> — {rec.policy_title}</span>
                        )}
                        {rec.cfr_section && (
                          <span className="text-blue-500 font-medium">{rec.cfr_section}</span>
                        )}
                        <span>{timeAgo(rec.created_at)}</span>
                      </div>
                    </div>

                    {/* SLA */}
                    <div className="flex-shrink-0 text-right">
                      {daysLeft !== null && rec.status === 'pending' && (
                        <div className={`text-xs font-semibold ${overdue ? 'text-red-600' : urgent ? 'text-amber-600' : 'text-slate-400'}`}>
                          {overdue ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft}d left`}
                        </div>
                      )}
                      <div className="text-xs text-slate-300 mt-0.5">SLA</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <DocumentLog />
      </div>
    </div>
  );
}
